/**
 * ============================================================================
 *   Copyright (c) 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * ============================================================================
 *   Module       : tokens
 *   Feature      : 197-portal-light-theme-responsive
 *   Description  : Typed accessors for the single design-token source of truth
 *                  (the CSS custom properties declared in `src/index.css`).
 *                  Light values are Azure-aligned; dark values are the existing
 *                  `.dark` overrides (unchanged). MUI and Chakra themes are
 *                  derived from these tokens so the three styling layers share
 *                  one source. Components reference tokens via `token(name)`
 *                  (→ `var(--…)`); runtime resolution uses `readToken(name)`.
 *   Constitution : v1.19.0 (Principle XLII intent — one canonical definition)
 *   Author       : Peter Dobler
 *   Created      : 2026-06-13
 * ============================================================================
 */

/** Canonical semantic color tokens (the only colors components may reference). */
export const SEMANTIC_COLOR_TOKENS = [
  '--color-bg-page',
  '--color-bg-card',
  '--color-bg-subtle',
  '--color-text-primary',
  '--color-text-secondary',
  '--color-border',
  '--color-accent',
  '--color-status-success',
  '--color-status-warning',
  '--color-status-danger',
  '--color-status-info',
] as const;

export type SemanticColorToken = (typeof SEMANTIC_COLOR_TOKENS)[number];

/** The one canonical accent token (Azure command-blue). Replaces `--color-brand`. */
export const ACCENT_TOKEN = '--color-accent' as const;

/** Capacity-type color tokens (preserved from the existing palette). */
export const CAPACITY_COLOR_TOKENS = [
  '--color-capacity-Platform',
  '--color-capacity-Reference',
  '--color-capacity-ClientDedicated',
  '--color-capacity-MultiTenant',
] as const;

/**
 * A CSS reference to a token, for use in styles: `token('--color-accent')`
 * → `'var(--color-accent)'`.
 */
export function token(name: string): string {
  return `var(${name})`;
}

/** Convenience: the accent as a CSS `var(...)` reference. */
export function accent(): string {
  return token(ACCENT_TOKEN);
}

/**
 * Resolve a token's *computed* value at runtime (e.g. for canvas / charts that
 * need a concrete color). Prefers an inline override, then the cascaded value,
 * then the supplied fallback. DOM-safe (returns fallback off-DOM).
 */
export function readToken(name: string, fallback = ''): string {
  if (typeof document === 'undefined') return fallback;
  const root = document.documentElement;
  const inline = root.style.getPropertyValue(name).trim();
  if (inline) return inline;
  const computed = getComputedStyle(root).getPropertyValue(name).trim();
  return computed || fallback;
}
